import * as React from 'react'
import { useEffect } from 'react'
import { Modal, Row } from 'react-bootstrap'
import { graphql } from 'gatsby'
import Layout from 'src/components/Layout'
import ContentContainer from 'src/components/content/ContentContainer/ContentContainer'
import { Filters } from 'src/components/filter/Filters/Filters'
import { ProductListing } from 'src/components/products/ProductListing/ProductListing'
import AboutContent from 'src/components/sections/AboutContent/AboutContent'
import BuyersGuideSection from 'src/components/sections/BuyersGuideSection/BuyersGuideSection'
import FeaturedProductsCarousel from 'src/components/sections/FeaturedProductCarousel/FeaturedProductCarousel'
import ProductTypesCardSection from 'src/components/sections/ProductTypesCardSection/ProductTypesCardSection'
import { Seo } from 'src/components/seo'
import { SearchProvider } from 'src/context/search-provider'
import CrossIcon from 'src/icons/cross'
import FilterIcon from 'src/icons/filter'
import SortIcon from 'src/icons/sort'
import { container, heading, paragraph } from 'src/pages/404.module.css'
import { Pagination } from 'src/pages/search'
import {
  activeFilters,
  filterButton,
  filterStyle,
  filterTitle,
  filterWrap,
  main,
  mobileFilterStyle,
  numberOfResults,
  results,
  search,
  sortIcon,
  sortSelector,
  stickyFilter,
} from 'src/pages/search-page.module.css'
import { getValuesFromQueryString, useCollectionFilter } from 'src/utils/hooks'

export default function ProductTypeIndex(props) {
  const data = props.data
  const category = data.contentfulProductCategoryLanding
  const title = category ? category.heroTitle : ''
  const seoTitle = category ? category.title : ''
  const seoDescription = category ? category.seoDescription : ''
  const metaDescription = category ? category.metaDescription : ''

  const bestSellers = data.bestSellers
  const featuredDeals = data.featuredDeals

  //About Content
  let aboutContent
  if (category) {
    if (category.aboutTitle && category.aboutContent.childMarkdownRemark) {
      aboutContent = {
        title: category.aboutTitle,
        text: category.aboutContent.childMarkdownRemark,
      }
    }
  }

  let types
  if (category) {
    if (category.types) {
      types = {
        data: category.types,
        title: category.typesSectionTitle,
        description: category.typesSectionDescription,
        types: category.types,
      }
    }
  }

  //Buyer Guide
  const buyersGuides = category
    ? category.buyingGuides
      ? category.buyingGuides
      : false
    : false

  return (
    <SearchProvider>
      <Layout>
        {!props.data.shopifyCollection ? (
          <div className={container}>
            <h1 className={heading}>Page Not Found</h1>
            <p className={paragraph}>
              {"Sorry, we couldn't find what you were looking for"}
            </p>
          </div>
        ) : (
          <>
            <Seo
              title={`${seoTitle} | ${seoDescription}`}
              description={metaDescription}
            />
            <ContentContainer className='ps-lg-0 py-3 ps-3'>
              <h1>{title}</h1>
            </ContentContainer>

            <CollectionPage {...props} />

            {aboutContent ? <AboutContent content={aboutContent} /> : ''}
            <Row className='py-5'>
              <FeaturedProductsCarousel
                featuredProducts={bestSellers}
                title={'Best Sellers'}
              />
            </Row>
            <Row className='py-5'>
              <FeaturedProductsCarousel
                featuredProducts={featuredDeals}
                title={'Fetaured Deals'}
              />
            </Row>
            {/* {faqs ? <FaqSection title={faqSectionTitle} faqs={faqs} /> : ''} */}
            {types ? <ProductTypesCardSection content={types} /> : ''}
            {buyersGuides ? <BuyersGuideSection content={buyersGuides} /> : ''}
          </>
        )}
      </Layout>
    </SearchProvider>
  )
}

function CollectionPage({ data, location }) {
  //data
  const collection = data.shopifyCollection

  //Product Listing
  const products = collection.products

  const queryParams = getValuesFromQueryString(location.search)
  const [filters, setFilters] = React.useState(queryParams)

  // This modal is only used on mobile
  const [showModal, setShowModal] = React.useState(false)
  const [filterOptions, setFilterOptions] = React.useState([])
  const [pause, setPause] = React.useState(true)
  const [sortKey, setSortKey] = React.useState('RELEVANCE')
  const containerRef = React.useRef()

  const {
    products: filteredProductHandles,
    isFetching,
    filterCount,
    filterCounts,
    hasNextPage,
    hasPreviousPage,
    fetchNextPage,
    fetchPreviousPage,
  } = useCollectionFilter(
    filters,
    sortKey,
    pause,
    15, // Products per page
    collection.handle,
  )

  useEffect(() => {
    setPause(false)
  }, [filters, sortKey])

  if (!pause && !isFetching && filterCounts && filterCounts.length > 0) {
    setFilterOptions(filterCounts)
    setPause(true)
  }

  let filteredProducts = React.useMemo(() => {
    if (filteredProductHandles == null) {
      return products
    }
    let result = products.filter((val) =>
      filteredProductHandles.includes(val.handle),
    )
    result.sort(
      (a, b) =>
        filteredProductHandles.indexOf(a.handle) -
        filteredProductHandles.indexOf(b.handle),
    )
    return result
  }, [filteredProductHandles])

  let productCount = React.useMemo(() => {
    if (!filterCounts || filterCount == 0) {
      return products.length
    }
    let max = 0
    filterCounts.map((filter) => {
      filter.values.map((option) => {
        const checkedFilters = filters[`metafield.${filter.key}`]
        if (checkedFilters != null && checkedFilters.includes(option.label)) {
          max = Math.max(max, option.count)
        }
      })
    })
    return max
  }, [filterCounts])

  return (
    <ContentContainer>
      <div ref={containerRef} className={main}>
        <div className={search} aria-hidden={showModal}>
          <div className={numberOfResults}>Showing {productCount} Results</div>
          <button
            className={[
              filterButton,
              filterCount ? activeFilters : undefined,
            ].join(' ')}
            onClick={() => setShowModal((show) => !show)}
            // This is hidden because the filters are already visible to
            // screenreaders, so the modal isnt needed.
          >
            <div className=''>
              <FilterIcon />
              <span>Filter</span>
            </div>
          </button>
          <div className={sortSelector}>
            <label aria-label='Sort'>
              <select
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value)}
                aria-label='Sort'
              >
                <optgroup label='Sort By:'>
                  <option value='RELEVANCE'>Relevance</option>
                  <option value='PRICE'>Price</option>
                  <option value='TITLE'>Title</option>
                  <option value='CREATED'>New items</option>
                  <option value='BEST_SELLING'>Trending</option>
                </optgroup>
              </select>
            </label>
            <SortIcon className={sortIcon} />
            <span>Sort</span>
          </div>
        </div>
        <section className={[filterStyle].join(' ')}>
          <div className={[stickyFilter].join(' ')}>
            <div className={filterTitle}>
              <h2 className='mb-0'>Filter</h2>
              <button
                aria-hidden={showModal}
                onClick={() => setShowModal(false)}
                aria-label='Filter'
              >
                <CrossIcon />
              </button>
            </div>
            <div className={`border-top ${filterWrap}`}>
              <Filters
                setFilters={setFilters}
                filters={filters}
                metafieldFilters={filterOptions}
              />
            </div>
          </div>
        </section>
        <Modal
          show={showModal}
          fullscreen={true}
          onHide={() => setShowModal(false)}
        >
          <Modal.Header>
            <h2 className='mb-0'>Filter</h2>
            <button
              className='btn-buy p-1 px-2'
              style={{ letterSpacing: '2px' }}
              onClick={() => {
                setShowModal(false)
              }}
            >
              See {productCount} results
            </button>
          </Modal.Header>
          <Modal.Body className={`p-3 ${mobileFilterStyle}`}>
            <Filters
              setFilters={setFilters}
              filters={filters}
              metafieldFilters={filterOptions}
            />
          </Modal.Body>
        </Modal>
        <section className={results}>
          <ProductListing products={filteredProducts} collection={collection} />
          {hasPreviousPage || hasNextPage ? (
            <Pagination
              previousPage={() => {
                const y =
                  containerRef.current.getBoundingClientRect().top +
                  window.scrollY -
                  150
                window.scrollTo({ top: y, behavior: 'auto' })
                setPause(false)
                fetchPreviousPage()
              }}
              hasPreviousPage={hasPreviousPage}
              nextPage={() => {
                const y =
                  containerRef.current.getBoundingClientRect().top +
                  window.scrollY -
                  150
                window.scrollTo({ top: y, behavior: 'auto' })
                setPause(false)
                fetchNextPage()
              }}
              hasNextPage={hasNextPage}
            />
          ) : undefined}
        </section>
      </div>
    </ContentContainer>
  )
}

export const query = graphql`
  query CollectionQuery($handle: String!) {
    shopifyCollection(handle: { eq: $handle, ne: "hidden" }) {
      id
      title
      handle
      metafields {
        value
        namespace
        key
      }
      products {
        id
        shopifyId
        tags
        title
        handle
        createdAt(fromNow: true)
        publishedAt
        productType
        collections {
          title
          handle
        }
        metafields {
          key
          value
        }
        vendor
        descriptionHtml
        media {
          ... on ShopifyMediaImage {
            id
            image {
              gatsbyImageData(aspectRatio: 1)
            }
          }
        }
        options {
          name
          values
        }
        variants {
          id
          title
          price
          compareAtPrice
          inventoryQuantity
          selectedOptions {
            name
            value
          }
          media {
            ... on ShopifyMediaImage {
              id
              image {
                gatsbyImageData(aspectRatio: 1)
              }
            }
          }
          metafields {
            key
            value
          }
        }
        priceRangeV2 {
          minVariantPrice {
            amount
            currencyCode
          }
        }
      }
    }
    bestSellers: shopifyCollection(handle: { eq: "best-sellers" }) {
      title
      handle
      products {
        title
        productType
        media {
          ... on ShopifyMediaImage {
            image {
              gatsbyImageData
            }
          }
        }
        metafields {
          key
          value
        }
        options {
          name
          values
        }
        tags
        variants {
          price
          compareAtPrice
          inventoryQuantity
          selectedOptions {
            name
            value
          }
          metafields {
            key
            value
          }
          image {
            gatsbyImageData
          }
          title
        }
        handle
        priceRangeV2 {
          minVariantPrice {
            amount
            currencyCode
          }
        }
      }
    }
    featuredDeals: shopifyCollection(handle: { eq: "featured-deals" }) {
      title
      handle
      products {
        title
        productType
        media {
          ... on ShopifyMediaImage {
            image {
              gatsbyImageData
            }
          }
        }
        metafields {
          key
          value
        }
        options {
          name
          values
        }
        tags
        variants {
          price
          compareAtPrice
          inventoryQuantity
          selectedOptions {
            name
            value
          }
          metafields {
            key
            value
          }
          image {
            gatsbyImageData
          }
          title
        }
        handle
        priceRangeV2 {
          minVariantPrice {
            amount
            currencyCode
          }
        }
      }
    }
    contentfulProductCategoryLanding(category: { url: { eq: $handle } }) {
      category {
        url
      }
      title
      seoDescription
      metaDescription
      heroTitle
      heroDescription {
        heroDescription
      }
      heroImage {
        gatsbyImageData
      }
      shopSectionTitle
      shopSectionDescription {
        shopSectionDescription
      }
      shopThumbnailes {
        title
        image {
          title
          gatsbyImageData
        }
        url
      }
      aboutTitle
      aboutContent {
        childMarkdownRemark {
          html
        }
      }
      typesSectionTitle
      typesSectionDescription {
        typesSectionDescription
      }
      types {
        title
        description {
          description
        }
        image {
          title
          gatsbyImageData
        }
        url
      }
      guidesSectionTitle
      guidesSectionDescription {
        guidesSectionDescription
      }
      buyingGuides {
        title
        description {
          description
        }
        image {
          gatsbyImageData
        }
        url
      }
    }
  }
`
